import { redirect } from '@tanstack/react-router';

/**
 * Settings cards that grew into pages of their own, by the anchor that addressed them.
 *
 * `/settings#price-book` is a promise: it has been pasted into emails and help docs. When the
 * price book moved to Products & services (CRM-18), the link kept working by landing on the
 * same card, under the same anchor, on its new page. `settings-anchors.test.ts` holds every
 * promised anchor to either a Settings card or an entry here, and runs the redirect.
 */
export const MOVED_FROM_SETTINGS = { 'price-book': '/products' } as const;

type MovedAnchor = keyof typeof MOVED_FROM_SETTINGS;

// An own key only: `constructor` and its kin are on every object, and are not moved cards.
const isMoved = (hash: string): hash is MovedAnchor =>
  Object.hasOwn(MOVED_FROM_SETTINGS, hash);

/**
 * The `/settings` route's `beforeLoad`: a moved anchor leaves for its new page before Settings
 * draws. The router replaces the history entry on a `beforeLoad` redirect, whatever the
 * redirect asks, so Back never returns to a URL that only redirects again. Every other hash,
 * and none, stays on Settings.
 */
export function leaveForMovedCard({ location }: { location: { hash: string } }): void {
  const { hash } = location;
  if (isMoved(hash)) throw redirect({ to: MOVED_FROM_SETTINGS[hash], hash });
}
